/**
 * DoctorWebsite.js — Production doctor profile (live `/dr/{slug}` route).
 *
 * As of Feb 9, 2026: this page is a thin SEO + structured-data wrapper around
 * `<DoctorWebsiteMockB productionMode />`. The full Zocdoc-style redesign,
 * hospital attachments, Leaflet map, two-stage TQS, and travel-time widget
 * all ship from Mock B — but production carries the indexable Helmet meta
 * tags, the full `@graph` JSON-LD (Physician + MedicalWebPage + BreadcrumbList,
 * extended with `geo` + `affiliation`), demo banner, and SEO articles section.
 *
 * Note: case studies + premium clinic announcement + AI articles previously
 * lived inside this file. They are scheduled to be re-added to Mock B as
 * dedicated sections in a follow-on iteration. Keeping the data fetches here
 * so once Mock B accepts a `productionExtras` slot, the data is already loaded.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import DoctorWebsiteMockB from './DoctorWebsiteMockB';

function procSlug(name) {
  return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function DoctorWebsite() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [seoMeta, setSeoMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // QR-Hook auto-save (Feb 2026): if a logged-in patient arrives here from a
  // doctor's printed QR poster (`?src=clinic-qr`), silently seed the clinic
  // into their MyClinics. Logged-out scanners still go through the existing
  // register-time auto-save flow. De-duped per (slug, session) via
  // sessionStorage so refreshing the page doesn't re-fire.
  useEffect(() => {
    if (!slug || !user || user.role !== 'patient') return;
    if (searchParams.get('src') !== 'clinic-qr') return;
    const sessionKey = `qr_autosave_${slug}`;
    try { if (sessionStorage.getItem(sessionKey) === '1') return; } catch { /* private mode */ }
    api.post('/patient/save-clinic', { doctor_slug: slug })
      .then(() => {
        try { sessionStorage.setItem(sessionKey, '1'); } catch { /* private mode */ }
      })
      .catch(() => { /* silent — non-blocking UX */ });
  }, [slug, user, searchParams]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/doctor/${slug}`)
      .then(r => {
        setDoctor(r.data);
        try { localStorage.setItem(`docsite_dr_${slug}`, JSON.stringify(r.data)); } catch { /* quota */ }
      })
      .catch(() => {
        try {
          const cached = localStorage.getItem(`docsite_dr_${slug}`);
          if (cached) setDoctor(JSON.parse(cached));
          else setError('Doctor not found');
        } catch { setError('Doctor not found'); }
      })
      .finally(() => setLoading(false));
    api.get(`/seo/meta/${slug}`).then(r => setSeoMeta(r.data)).catch(() => {});
  }, [slug]);

  // Resolve geo coordinates for the primary clinic (used for JSON-LD `geo`).
  // Falls back to the cached lat/lon already on the location doc when present.
  const [primaryGeo, setPrimaryGeo] = useState(null);
  useEffect(() => {
    const primary = doctor?.locations?.[0];
    if (!primary?.id) return;
    if (typeof primary.lat === 'number' && typeof primary.lon === 'number') {
      setPrimaryGeo({ lat: primary.lat, lon: primary.lon });
      return;
    }
    api.get(`/public/clinic-geo/${primary.id}`)
      .then(r => setPrimaryGeo({ lat: r.data.lat, lon: r.data.lon }))
      .catch(() => {});
  }, [doctor?.locations]);

  const seoData = useMemo(() => {
    if (!doctor) return null;
    const origin = (typeof window !== 'undefined') ? window.location.origin : 'https://docsite.care';
    const profileUrl = `${origin}/dr/${slug}`;
    const primaryCity = doctor.locations?.[0]?.city || '';
    const primarySpec = doctor.specialization_names?.[0] || '';
    const cityPart = primaryCity ? ` in ${primaryCity}` : '';
    const specPart = primarySpec ? `${primarySpec} ` : '';
    const fallbackTitle = `${doctor.name} — ${specPart}${primaryCity || 'Doctor'} | Book Appointment`;
    const fallbackDesc =
      `${doctor.name}${cityPart}, ${primarySpec || 'practising doctor'}. ${doctor.qualifications || ''}` +
      `${doctor.experience_years ? ` · ${doctor.experience_years}+ yrs experience` : ''}. Book your appointment online.`;
    const photoBackend = process.env.REACT_APP_BACKEND_URL || '';
    const photoSrc = doctor.photo_url?.startsWith('/api/')
      ? `${photoBackend}${doctor.photo_url}` : doctor.photo_url;
    return { origin, profileUrl, primaryCity, primarySpec, fallbackTitle, fallbackDesc, photoSrc };
  }, [doctor, slug]);

  const jsonLdGraph = useMemo(() => {
    if (!doctor || !seoData) return null;
    const { origin, profileUrl, primarySpec, photoSrc } = seoData;
    const sameAs = [
      doctor.gbp_url, doctor.linkedin_url, doctor.practo_url,
      doctor.justdial_url, doctor.lybrate_url, doctor.scholar_url,
    ].filter(Boolean);

    // PostalAddress array for every location.
    const addresses = (doctor.locations || []).map(loc => ({
      "@type": "PostalAddress",
      "streetAddress": loc.address || '',
      "addressLocality": loc.city || '',
      "addressRegion": loc.state || '',
      "postalCode": loc.pincode || '',
      "addressCountry": "IN",
    }));

    // Geo lat/lon — primary clinic only. Local-SEO uplift for "near me" queries.
    const geo = primaryGeo ? {
      "@type": "GeoCoordinates",
      "latitude": primaryGeo.lat,
      "longitude": primaryGeo.lon,
    } : undefined;

    // Hospital affiliations — verified entries become MedicalOrganization
    // nodes with accreditation. DpD entries are still listed but without the
    // accreditation array (they aren't yet validated).
    const affiliation = (doctor.hospital_attachments || []).map(h => {
      const node = {
        "@type": "MedicalOrganization",
        "name": h.hospital_name,
        ...(h.city ? {
          "address": { "@type": "PostalAddress", "addressLocality": h.city, "addressCountry": "IN" }
        } : {}),
      };
      if ((h.accreditation || []).length > 0) {
        node.hasCredential = h.accreditation.map(a => ({
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "accreditation",
          "name": a,
        }));
      }
      return node;
    });

    const physician = {
      "@type": "Physician",
      "@id": `${profileUrl}#physician`,
      "name": doctor.name,
      "description": doctor.bio || `${doctor.name} — ${doctor.qualifications || primarySpec}`,
      "medicalSpecialty": doctor.specialization_names?.join(', ') || primarySpec,
      "image": photoSrc || `${origin}/logo512.png`,
      "telephone": doctor.phone || '',
      "url": profileUrl,
      "knowsLanguage": doctor.languages || ["en", "hi"],
      ...(doctor.qualifications ? { "hasCredential": doctor.qualifications } : {}),
      ...(doctor.experience_years ? { "yearsOfExperience": doctor.experience_years } : {}),
      ...(addresses.length > 0 ? { "address": addresses } : {}),
      ...(geo ? { "geo": geo } : {}),
      ...(doctor.tqs?.count > 0 && doctor.tqs?.star_rating ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": doctor.tqs.star_rating,
          "bestRating": 5,
          "ratingCount": doctor.tqs.count,
          "reviewCount": doctor.tqs.count,
        },
      } : (doctor.rating?.score && doctor.rating?.ready ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": doctor.rating.score,
          "bestRating": 5,
          "ratingCount": doctor.rating.total_feedback || 1,
        },
      } : {})),
      ...(doctor.trained_at?.hospital_name ? {
        "alumniOf": {
          "@type": "MedicalOrganization",
          "name": doctor.trained_at.hospital_name,
          ...(doctor.trained_at.location ? { "address": doctor.trained_at.location } : {}),
        },
      } : {}),
      ...(affiliation.length > 0 ? { "affiliation": affiliation } : {}),
      ...(sameAs.length > 0 ? { "sameAs": sameAs } : {}),
      ...(doctor.procedures?.length > 0 ? {
        "availableService": doctor.procedures.map(p => ({
          "@type": "MedicalProcedure",
          "name": p.name,
          "url": `${profileUrl}/procedures/${procSlug(p.name)}`,
        })),
      } : {}),
    };

    const webPage = {
      "@type": "MedicalWebPage",
      "@id": `${profileUrl}#webpage`,
      "url": profileUrl,
      "name": `${doctor.name}${primarySpec ? ' — ' + primarySpec : ''}${seoData.primaryCity ? ' in ' + seoData.primaryCity : ''}`,
      "isPartOf": { "@id": `${origin}/#website` },
      "primaryImageOfPage": photoSrc ? { "@type": "ImageObject", "url": photoSrc } : undefined,
      "about": { "@id": `${profileUrl}#physician` },
      "inLanguage": "en-IN",
      "potentialAction": {
        "@type": "ReserveAction",
        "target": `${profileUrl}#book`,
        "name": `Book appointment with ${doctor.name}`,
      },
    };

    const breadcrumb = {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "DocSite", "item": `${origin}/` },
        ...(primarySpec ? [{
          "@type": "ListItem", "position": 2, "name": primarySpec,
          "item": `${origin}/find/${(doctor.specializations?.[0] || '').toLowerCase()}`,
        }] : []),
        { "@type": "ListItem", "position": primarySpec ? 3 : 2, "name": doctor.name, "item": profileUrl },
      ],
    };

    return { "@context": "https://schema.org", "@graph": [physician, webPage, breadcrumb] };
  }, [doctor, primaryGeo, seoData]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  if (error || !doctor) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><p className="text-slate-500">{error || 'Doctor not found'}</p></div>;

  const isDemo = !!doctor.is_demo;

  // ── PUBLISH GATE (founder rule, May 2026) ────────────────────────────
  // Free / Coffee / Turbo — every tier must clear the 7-item Publish Gate
  // checklist (photo, bio ≥ 50, qualifications, registration #, 1
  // specialization, 1 clinic, weekly schedule, 3 procedures, 1 priced
  // procedure, 40% voice-authentic edits). Until then, the public URL
  // renders a clean "Profile under construction" placeholder — Google
  // sees noindex, patients see a respectful holding page, and DocSite's
  // domain authority is never dragged down by a thin half-empty profile.
  // Demo profiles are exempt (they're intentionally thin sample data).
  const publishBlocked = !isDemo && doctor.is_publishable === false;
  if (publishBlocked) {
    const docName = (doctor.name || '').replace(/^Dr\.?\s+/i, '');
    return (
      <>
        <Helmet>
          <title>{`Profile under construction — Dr. ${docName} | DocSite`}</title>
          <meta name="robots" content="noindex, follow" />
          <meta name="description" content={`Dr. ${docName}'s DocSite profile is being prepared. Please check back shortly.`} />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/40 to-emerald-50/30 flex items-center justify-center p-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center" data-testid="publish-gate-placeholder">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-50 border border-sky-200 mb-4">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-sky-600"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-700">Profile under construction</p>
            <h1 className="text-2xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="publish-gate-doctor-name">
              Dr. {docName}
            </h1>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              This DocSite profile is still being completed by the doctor. To protect medical-content quality, DocSite only publishes profiles that meet the full readiness checklist (photo, bio, qualifications, clinic details, schedule, procedures).
            </p>
            <p className="text-xs text-slate-500 mt-4">Please check back in a day or two — or contact the clinic directly if you have an appointment.</p>
            <div className="mt-6 pt-5 border-t border-slate-100">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Why this gate exists</p>
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">DocSite never publishes thin or half-filled doctor pages. Every live profile carries verified credentials, real clinic details, and a clear treatment menu — so patients can trust what they read.</p>
            </div>
            <p className="text-[10px] text-slate-400 mt-6">Powered by DocSite&trade;</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* SEO Meta Tags — Lever 1: own branded "Dr Name City Specialty" search */}
      {seoData && (
        <Helmet>
          <title>{seoMeta?.meta_title || seoData.fallbackTitle}</title>
          <meta name="description" content={seoMeta?.meta_description || seoData.fallbackDesc} />
          {seoMeta?.keywords?.length > 0 && <meta name="keywords" content={seoMeta.keywords.join(', ')} />}
          <meta property="og:title" content={seoMeta?.meta_title || seoData.fallbackTitle} />
          <meta property="og:description" content={seoMeta?.meta_description || seoData.fallbackDesc} />
          <meta property="og:type" content="profile" />
          <meta property="og:locale" content="en_IN" />
          {seoData.photoSrc && <meta property="og:image" content={seoData.photoSrc} />}
          <meta name="twitter:card" content={seoData.photoSrc ? 'summary_large_image' : 'summary'} />
          <meta name="twitter:title" content={seoMeta?.meta_title || seoData.fallbackTitle} />
          <meta name="twitter:description" content={seoMeta?.meta_description || seoData.fallbackDesc} />
          {seoData.photoSrc && <meta name="twitter:image" content={seoData.photoSrc} />}
          {/* Robots: only emit explicit noindex for is_demo or for doctors
              that have *explicitly* opted out (is_publishable === false).
              Default (null/undefined/true) → fully indexable. */}
          {isDemo && <meta name="robots" content="noindex, nofollow" />}
          {!isDemo && doctor.is_publishable === false && <meta name="robots" content="noindex, follow" />}
          {!isDemo && doctor.is_publishable !== false && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" /> }
          <link rel="canonical" href={`${seoData.origin}/dr/${slug}`} />
        </Helmet>
      )}

      {/* Schema.org @graph — Physician + MedicalWebPage + BreadcrumbList. Now
          carries `geo` (primary clinic lat/lon) and `affiliation` (verified
          NABH/JCI hospital attachments) for local-SEO uplift. */}
      {jsonLdGraph && (
        <Helmet>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdGraph) }} />
        </Helmet>
      )}

      {/* Demo / Dummy site banner — only on demo profiles */}
      {isDemo && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-xs text-center py-2 font-semibold sticky top-0 z-50">
          Demo profile · sample data · not a real practising doctor
        </div>
      )}

      {/* Production body — same redesign that ships at /preview-b */}
      <DoctorWebsiteMockB productionMode />
    </>
  );
}
